import { analytics } from '@commander/product-analytics';
import { ExternalLinkIcon } from '@commander/ui';
import {
  RiArrowDownLine,
  RiCheckLine,
  RiCloseLine,
  RiFileCopyLine,
  RiRefreshLine,
  RiThumbDownFill,
  RiThumbDownLine,
  RiThumbUpFill,
  RiThumbUpLine,
} from '@remixicon/react';
import type { UIMessage } from 'ai';
import { getToolOrDynamicToolName, isToolOrDynamicToolUIPart } from 'ai';
import { motion } from 'motion/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { findPriorAnswer, partToBlockEnvelope } from '../generative-ui/adapter.js';
import { BlockRenderer } from '../generative-ui/BlockRenderer.js';
import { isUserAnswerPart } from '../generative-ui/blocks/question.js';
import { sanitizeHref } from '../generative-ui/components/utils.js';
import { useCommanderGoAnalytics } from '../hooks/useCommanderGoAnalytics.js';
import { useDeleteFeedback, useSubmitFeedback } from '../hooks/useSubmitFeedback.js';
import { TextShimmer, ToolCallDetails } from './ToolCallDetails.js';
import { WelcomeScreen } from './WelcomeScreen.js';

const FEEDBACK_CATEGORIES = [
  { value: 'incorrect_data', label: 'Incorrect data' },
  { value: 'wrong_reasoning', label: 'Wrong reasoning' },
  { value: 'not_helpful', label: 'Not helpful' },
  { value: 'slow', label: 'Too slow' },
  { value: 'formatting', label: 'Bad formatting' },
  { value: 'other', label: 'Other' },
] as const;

function ScrollableTable(props: React.ComponentProps<'table'>) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [overflows, setOverflows] = useState(false);
  const [atRightEdge, setAtRightEdge] = useState(false);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const check = () => {
      const isOverflowing = el.scrollWidth > el.clientWidth + 1;
      setOverflows(isOverflowing);
      setAtRightEdge(el.scrollLeft + el.clientWidth >= el.scrollWidth - 1);
    };
    check();
    const resizeObserver = new ResizeObserver(check);
    resizeObserver.observe(el);
    el.addEventListener('scroll', check, { passive: true });
    return () => {
      resizeObserver.disconnect();
      el.removeEventListener('scroll', check);
    };
  }, []);

  return (
    <div className="my-2 -mx-3">
      <div ref={scrollRef} className="overflow-x-auto px-3 pb-2">
        <table className="min-w-full border-collapse text-sm" {...props} />
      </div>
      {overflows && !atRightEdge && (
        <div className="px-3 text-[10px] text-muted-foreground text-right italic">
          scroll for more →
        </div>
      )}
    </div>
  );
}

const markdownComponents = {
  p: ({ ...props }: React.ComponentProps<'p'>) => (
    <p className="mb-2 last:mb-0" style={{ whiteSpace: 'pre-line' }} {...props} />
  ),
  ul: ({ ...props }: React.ComponentProps<'ul'>) => (
    <ul className="list-disc pl-4 mb-2" {...props} />
  ),
  ol: ({ ...props }: React.ComponentProps<'ol'>) => (
    <ol className="list-decimal pl-4 mb-2" {...props} />
  ),
  li: ({ ...props }: React.ComponentProps<'li'>) => <li className="mb-1" {...props} />,
  table: (props: React.ComponentProps<'table'>) => <ScrollableTable {...props} />,
  th: ({ ...props }: React.ComponentProps<'th'>) => (
    <th
      className="border-b border-r last:border-r-0 border-border bg-muted/50 px-3 py-1.5 text-left text-xs font-semibold whitespace-nowrap"
      {...props}
    />
  ),
  td: ({ ...props }: React.ComponentProps<'td'>) => (
    <td
      className="border-b border-r last:border-r-0 border-border px-3 py-1.5 text-xs whitespace-nowrap"
      {...props}
    />
  ),
  strong: ({ ...props }: React.ComponentProps<'strong'>) => (
    <strong className="font-semibold" {...props} />
  ),
  a: ({ href, children, ...props }: React.ComponentProps<'a'>) => {
    const safeHref = sanitizeHref(href);
    if (!safeHref) {
      return <span className="text-foreground">{children}</span>;
    }
    const isDeepLink = safeHref.startsWith('/crm/') || safeHref.startsWith('/prospecting/');
    if (isDeepLink) {
      return (
        <a
          href={safeHref}
          className="inline-flex items-center gap-1 rounded-md border border-primary-200 bg-primary-50 px-1.5 py-0.5 text-xs font-medium text-primary-700 no-underline hover:bg-primary-100 dark:border-primary-800 dark:bg-primary-950 dark:text-primary-300 dark:hover:bg-primary-900"
          {...props}
        >
          {children}
          <ExternalLinkIcon className="h-3 w-3 shrink-0" />
        </a>
      );
    }
    return (
      <a href={safeHref} className="text-primary-600 hover:text-primary-700 underline" {...props}>
        {children}
      </a>
    );
  },
};

function formatTimestamp(date?: Date | string): string {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfYesterday = new Date(startOfToday);
  startOfYesterday.setDate(startOfYesterday.getDate() - 1);

  if (d >= startOfToday) return time;
  if (d >= startOfYesterday) return `Yesterday, ${time}`;

  const sameYear = d.getFullYear() === now.getFullYear();
  const dateStr = d.toLocaleDateString([], {
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
  return `${dateStr}, ${time}`;
}

function getMessageText(parts: UIMessage['parts']): string {
  return parts
    .filter((p) => p.type === 'text')
    .map((p) => (p.type === 'text' ? p.text : ''))
    .join('\n');
}

function MessageActions({
  message,
  isLast,
  isUser,
  onRetry,
  appContext,
  analyticsCtx,
  onPersistFeedback,
  onClearFeedback,
  feedback,
  onFeedbackSet,
  createdAt,
}: {
  message: UIMessage;
  isLast: boolean;
  isUser: boolean;
  onRetry?: () => void;
  appContext?: string;
  analyticsCtx?: { appContext?: string; threadId?: string } | null;
  onPersistFeedback?: (data: {
    thread_id: string;
    message_ui_id: string;
    rating: 'positive' | 'negative';
    category?: string;
    app_context?: string;
  }) => void;
  onClearFeedback?: (data: { thread_id: string; message_ui_id: string }) => void;
  feedback?: 'positive' | 'negative';
  onFeedbackSet?: (messageId: string, rating: 'positive' | 'negative' | undefined) => void;
  createdAt?: Date;
}) {
  const [copied, setCopied] = useState(false);
  const [showCategories, setShowCategories] = useState(false);

  const handleCopy = useCallback(() => {
    const text = getMessageText(message.parts);
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [message.parts]);

  const submitFeedback = useCallback(
    (rating: 'positive' | 'negative', category?: string) => {
      onFeedbackSet?.(message.id, rating);
      const resolvedAppContext = analyticsCtx?.appContext ?? appContext ?? 'unknown';
      const threadId = analyticsCtx?.threadId ?? '';

      analytics.track('commander_go_feedback_submitted', {
        app_context: resolvedAppContext,
        thread_id: threadId,
        assistant_message_id: message.id,
        rating,
        category,
        has_comment: false,
      });

      if (onPersistFeedback && threadId) {
        onPersistFeedback({
          thread_id: threadId,
          message_ui_id: message.id,
          rating,
          category,
          app_context: resolvedAppContext,
        });
      }
    },
    [analyticsCtx, appContext, message.id, onPersistFeedback, onFeedbackSet]
  );

  const clearFeedback = useCallback(() => {
    onFeedbackSet?.(message.id, undefined);
    setShowCategories(false);
    const threadId = analyticsCtx?.threadId ?? '';
    if (onClearFeedback && threadId) {
      onClearFeedback({ thread_id: threadId, message_ui_id: message.id });
    }
  }, [analyticsCtx, message.id, onClearFeedback, onFeedbackSet]);

  const handleThumbsUp = useCallback(() => {
    if (feedback === 'positive') {
      clearFeedback();
      return;
    }
    setShowCategories(false);
    submitFeedback('positive');
  }, [feedback, submitFeedback, clearFeedback]);

  const handleThumbsDown = useCallback(() => {
    if (feedback === 'negative') {
      clearFeedback();
      return;
    }
    // Don't clear an existing positive rating here — only open the category
    // picker. The actual transition to 'negative' happens in
    // handleCategorySelect once the user commits a category, so dismissing
    // the picker leaves the prior rating intact.
    setShowCategories(true);
  }, [feedback, clearFeedback]);

  const handleCategorySelect = useCallback(
    (category: string) => {
      submitFeedback('negative', category);
      setShowCategories(false);
    },
    [submitFeedback]
  );

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2 transition-opacity opacity-0 group-hover:opacity-100">
        <button
          type="button"
          className="text-muted-foreground hover:text-foreground transition-colors"
          onClick={handleCopy}
          title={copied ? 'Copied!' : 'Copy'}
        >
          {copied ? (
            <RiCheckLine className="h-3.5 w-3.5 text-green-600" />
          ) : (
            <RiFileCopyLine className="h-3.5 w-3.5" />
          )}
        </button>
        {message.role === 'assistant' && isLast && onRetry && (
          <button
            type="button"
            className="text-muted-foreground hover:text-foreground transition-colors"
            onClick={onRetry}
            title="Retry"
          >
            <RiRefreshLine className="h-3.5 w-3.5" />
          </button>
        )}
        {message.role === 'assistant' && !isUser && (
          <>
            <button
              type="button"
              className={`transition-colors ${feedback === 'positive' ? 'text-green-600' : 'text-muted-foreground hover:text-foreground'}`}
              onClick={handleThumbsUp}
              title={feedback === 'positive' ? 'Remove rating' : 'Good response'}
            >
              {feedback === 'positive' ? (
                <RiThumbUpFill className="h-3.5 w-3.5" />
              ) : (
                <RiThumbUpLine className="h-3.5 w-3.5" />
              )}
            </button>
            <button
              type="button"
              className={`transition-colors ${feedback === 'negative' ? 'text-red-500' : 'text-muted-foreground hover:text-foreground'}`}
              onClick={handleThumbsDown}
              title={feedback === 'negative' ? 'Remove rating' : 'Bad response'}
            >
              {feedback === 'negative' ? (
                <RiThumbDownFill className="h-3.5 w-3.5" />
              ) : (
                <RiThumbDownLine className="h-3.5 w-3.5" />
              )}
            </button>
          </>
        )}
        <span className="text-[10px] text-muted-foreground ml-1">
          {formatTimestamp(createdAt ?? (message as UIMessage & { createdAt?: Date }).createdAt)}
        </span>
      </div>
      {showCategories && feedback !== 'negative' && (
        <div className="flex flex-wrap items-center gap-1 animate-in fade-in slide-in-from-top-1 duration-150">
          <span className="text-[10px] text-muted-foreground mr-0.5">What went wrong?</span>
          {FEEDBACK_CATEGORIES.map((cat) => (
            <button
              key={cat.value}
              type="button"
              className="rounded-full border border-border bg-background px-2 py-0.5 text-[10px] text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
              onClick={() => handleCategorySelect(cat.value)}
            >
              {cat.label}
            </button>
          ))}
          <button
            type="button"
            className="text-muted-foreground hover:text-foreground transition-colors ml-0.5"
            onClick={() => setShowCategories(false)}
            title="Dismiss"
          >
            <RiCloseLine className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
    </div>
  );
}

interface MessageListProps {
  messages: UIMessage[];
  isLoading: boolean;
  onRetry?: () => void;
  onSend?: (text: string) => void;
  appContext?: 'crm' | 'prospecting';
  onLoadOlder?: () => void;
  hasPreviousMessages?: boolean;
  isLoadingOlder?: boolean;
}

function UserMessage({ parts }: { parts: UIMessage['parts'] }) {
  const textParts = parts.filter((p) => p.type === 'text');
  if (textParts.length === 0) return null;

  return (
    <div className="max-w-[85%] min-w-0 overflow-x-auto rounded-lg px-3 py-2 text-sm bg-primary text-primary-foreground whitespace-pre-wrap">
      {textParts.map((part) => (
        <span key={part.type === 'text' ? part.text : undefined}>
          {part.type === 'text' ? part.text : null}
        </span>
      ))}
    </div>
  );
}

function AssistantMessage({
  parts,
  messageId,
  messages,
  showTrailingShimmer,
}: {
  parts: UIMessage['parts'];
  messageId: string;
  messages: UIMessage[];
  showTrailingShimmer?: boolean;
}) {
  // Track which internal-tool shimmer names we've already rendered
  // so parallel queryData calls only show one "Searching Commander..." shimmer
  const renderedShimmers = new Set<string>();

  // Latest progress stage emitted by the backend (data-commander-progress part)
  let latestProgressStage: string | undefined;
  for (const part of parts) {
    if (part.type === 'data-commander-progress') {
      const data = (part as { data?: { stage?: string } }).data;
      if (data?.stage) latestProgressStage = data.stage;
    }
  }

  // Render parts in order to preserve text→block→text interleaving
  return (
    <div className="flex flex-col gap-1 min-w-0 w-full">
      {parts.map((part) => {
        if (part.type === 'text') {
          if (!part.text) return null;
          return (
            <div
              key={`text-${part.text}`}
              className="max-w-full min-w-0 px-3 py-2 text-sm text-foreground"
            >
              <div className="prose prose-sm dark:prose-invert max-w-none break-words overflow-x-auto">
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                  {part.text}
                </ReactMarkdown>
              </div>
            </div>
          );
        }

        if (isToolOrDynamicToolUIPart(part)) {
          const toolName = getToolOrDynamicToolName(part);
          const state = part.state;
          const data = state === 'output-available' ? part.output : part.input;
          const envelope = partToBlockEnvelope(toolName, state, data, part.toolCallId);

          if (envelope) {
            // For interactive `question` blocks, recover any prior answer from
            // a subsequent user message's `data-user-answer` part so the
            // selection survives thread reloads.
            let preSelectedId: string | null = null;
            if (
              envelope.type === 'question' &&
              !('loading' in envelope && envelope.loading) &&
              'data' in envelope &&
              envelope.data &&
              typeof envelope.data === 'object'
            ) {
              const options = (envelope.data as { options?: { id: string }[] }).options ?? [];
              const validIds = new Set(options.map((o) => o.id));
              preSelectedId = findPriorAnswer(messages, messageId, part.toolCallId, validIds);
            }

            // UI block — render at full width, outside text bubble
            return (
              <div key={`block-${part.toolCallId}`} className="w-full min-w-0 my-1">
                <BlockRenderer
                  envelope={envelope}
                  toolCallId={part.toolCallId}
                  preSelectedId={preSelectedId}
                />
              </div>
            );
          }

          // Internal tool (queryData) — show shimmer while loading, but only once per tool name
          if (state !== 'output-available') {
            if (renderedShimmers.has(toolName)) {
              return null;
            }
            renderedShimmers.add(toolName);
            return (
              <ToolCallDetails
                key={`tool-${part.toolCallId}`}
                toolName={toolName}
                state={state}
                progressStage={latestProgressStage}
              />
            );
          }

          // Internal tool finished — render nothing
          return null;
        }

        return null;
      })}
      {showTrailingShimmer && <TextShimmer duration={1.5}>Putting it together ...</TextShimmer>}
    </div>
  );
}

export function MessageList({
  messages,
  isLoading,
  onRetry,
  onSend,
  appContext,
  onLoadOlder,
  hasPreviousMessages,
  isLoadingOlder,
}: MessageListProps) {
  const bottomRef = useRef<HTMLDivElement>(null);
  const prevIdsRef = useRef<Set<string> | null>(null);
  const analyticsCtx = useCommanderGoAnalytics();
  const { mutate: persistFeedback } = useSubmitFeedback();
  const { mutate: deletePersistedFeedback } = useDeleteFeedback();
  const [feedbackMap, setFeedbackMap] = useState<Record<string, 'positive' | 'negative'>>({});

  // Track first-seen timestamp for each message (for live useChat messages that
  // don't have a createdAt). Loaded history messages carry their own createdAt.
  const firstSeenRef = useRef<Map<string, Date>>(new Map());
  for (const m of messages) {
    if (!firstSeenRef.current.has(m.id)) {
      firstSeenRef.current.set(m.id, new Date());
    }
  }

  const handleFeedbackSet = useCallback(
    (messageId: string, rating: 'positive' | 'negative' | undefined) => {
      setFeedbackMap((prev) => {
        const next = { ...prev };
        if (rating) {
          next[messageId] = rating;
        } else {
          delete next[messageId];
        }
        return next;
      });
    },
    []
  );

  // Seed with current messages on first render so loaded history doesn't animate
  if (prevIdsRef.current === null) {
    prevIdsRef.current = new Set(messages.map((m) => m.id));
  }

  // Detect thread switch: no overlap + both sides non-empty → re-seed
  const hasOverlap = messages.some((m) => prevIdsRef.current?.has(m.id));
  if (!hasOverlap && prevIdsRef.current.size > 0 && messages.length > 0) {
    prevIdsRef.current = new Set(messages.map((m) => m.id));
  }

  // Update snapshot after render so new messages animate only once
  useEffect(() => {
    prevIdsRef.current = new Set(messages.map((m) => m.id));
  }, [messages]);

  // Track whether the user can see the bottom of the list.
  // State-backed so useInView below can use it as intersection root (refs aren't reactive).
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const [scrollRoot, setScrollRoot] = useState<HTMLDivElement | null>(null);
  const setScrollContainer = useCallback((el: HTMLDivElement | null) => {
    scrollContainerRef.current = el;
    setScrollRoot(el);
  }, []);
  const [isAtBottom, setIsAtBottom] = useState(true);

  useEffect(() => {
    const sentinel = bottomRef.current;
    if (!sentinel || !scrollRoot) return;
    const observer = new IntersectionObserver(([entry]) => setIsAtBottom(entry.isIntersecting), {
      root: scrollRoot,
      threshold: 0,
    });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [scrollRoot]);

  // Sticky "follow mode" — once the user is at the bottom we lock in and keep
  // scrolling as content arrives (streamed tokens, late-rendering tables,
  // images loading). Manual scroll-up exits follow mode; clicking the
  // "New messages" button or sending a new message re-enters it.
  const isFollowingRef = useRef(true);
  const isProgrammaticScrollRef = useRef(false);
  // State-backed so the ResizeObserver effect re-runs when the main layout
  // first mounts (we render <WelcomeScreen> before any messages arrive, so
  // the content wrapper isn't in the tree on the first render).
  const [contentEl, setContentEl] = useState<HTMLDivElement | null>(null);

  const scrollContainerToBottom = useCallback((smooth = true) => {
    const el = scrollContainerRef.current;
    if (!el) return;
    isProgrammaticScrollRef.current = true;
    isFollowingRef.current = true;
    el.scrollTo({ top: el.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
    // Smooth scroll animates ~300ms; auto resolves next frame. Clearing after
    // 400ms is a safe upper bound for either — the scroll event fired during
    // the animation must be ignored so it doesn't flip `isFollowingRef` off.
    setTimeout(() => {
      isProgrammaticScrollRef.current = false;
    }, 400);
  }, []);

  // Detect manual scroll to exit/enter follow mode. Depends on `scrollRoot`
  // (the state mirror of the ref) so the listener re-attaches when the
  // scroll container element becomes available.
  useEffect(() => {
    if (!scrollRoot) return;
    const handleScroll = () => {
      if (isProgrammaticScrollRef.current) return;
      const distance = scrollRoot.scrollHeight - scrollRoot.clientHeight - scrollRoot.scrollTop;
      isFollowingRef.current = distance < 20;
    };
    scrollRoot.addEventListener('scroll', handleScroll, { passive: true });
    return () => scrollRoot.removeEventListener('scroll', handleScroll);
  }, [scrollRoot]);

  // Watch the content wrapper for any size growth (streamed tokens, tables,
  // images, etc.) and keep following while the user is in follow mode.
  useEffect(() => {
    if (!contentEl || !scrollRoot) return;
    const observer = new ResizeObserver(() => {
      if (!isFollowingRef.current) return;
      isProgrammaticScrollRef.current = true;
      scrollRoot.scrollTo({ top: scrollRoot.scrollHeight, behavior: 'auto' });
      requestAnimationFrame(() => {
        isProgrammaticScrollRef.current = false;
      });
    });
    observer.observe(contentEl);
    return () => observer.disconnect();
  }, [contentEl, scrollRoot]);

  // User just hit send — re-enter follow mode and scroll immediately.
  const prevMessageCountRef = useRef(messages.length);
  useEffect(() => {
    const isNewMessage = messages.length > prevMessageCountRef.current;
    prevMessageCountRef.current = messages.length;
    const last = messages[messages.length - 1];
    if (isNewMessage && last?.role === 'user') {
      scrollContainerToBottom();
    }
  }, [messages, scrollContainerToBottom]);

  // Thread switched (user opened a previous chat) — re-enter follow mode.
  // The jump to the bottom happens when the content mounts/grows (the
  // ResizeObserver above), since messages are often fetched async after the
  // threadId changes. We just flip the follow flag so that observer acts.
  const activeThreadId = analyticsCtx?.threadId;
  useEffect(() => {
    if (!activeThreadId) return;
    isFollowingRef.current = true;
    // If the content is already mounted with messages, scroll now; otherwise
    // the ResizeObserver fires as history renders.
    if (contentEl && scrollRoot) {
      scrollContainerToBottom(false);
    }
  }, [activeThreadId, contentEl, scrollRoot, scrollContainerToBottom]);

  const scrollToBottom = scrollContainerToBottom;

  // ── Infinite scroll for older messages (same pattern as SMS) ──
  // Observe intersection against the inner scroll container, not the viewport —
  // the messages list scrolls inside `overflow-y-auto`, so the sentinel would
  // otherwise stay permanently in view and either never fire or fire continuously.
  const { ref: loadMoreRef, inView: loadMoreInView } = useInView({
    threshold: 0,
    root: scrollRoot,
    rootMargin: '200px',
  });
  const prevScrollHeightRef = useRef(0);

  useEffect(() => {
    if (loadMoreInView && hasPreviousMessages && !isLoadingOlder && onLoadOlder) {
      onLoadOlder();
    }
  }, [loadMoreInView, hasPreviousMessages, isLoadingOlder, onLoadOlder]);

  // Save scroll height before fetching older messages
  useEffect(() => {
    if (isLoadingOlder && scrollContainerRef.current) {
      prevScrollHeightRef.current = scrollContainerRef.current.scrollHeight;
    }
  }, [isLoadingOlder]);

  // Restore scroll position after older messages are prepended
  useEffect(() => {
    if (scrollContainerRef.current && !isLoadingOlder && prevScrollHeightRef.current > 0) {
      const newScrollHeight = scrollContainerRef.current.scrollHeight;
      const scrollDiff = newScrollHeight - prevScrollHeightRef.current;
      if (scrollDiff > 0) {
        scrollContainerRef.current.scrollTop += scrollDiff;
      }
      prevScrollHeightRef.current = 0;
    }
  }, [isLoadingOlder]);

  if (messages.length === 0) {
    return <WelcomeScreen onSend={onSend} appContext={appContext} />;
  }

  // Find last assistant message index for retry button
  const lastAssistantIdx = messages.reduce(
    (acc, msg, idx) => (msg.role === 'assistant' ? idx : acc),
    -1
  );

  return (
    <div className="relative flex flex-col min-h-0 flex-1">
      <div ref={setScrollContainer} className="min-h-0 flex-1 overflow-y-auto">
        <div ref={setContentEl} className="flex flex-col gap-3 px-6 py-4">
          {hasPreviousMessages && (
            <div ref={loadMoreRef} className="py-2 text-center">
              {isLoadingOlder ? (
                <div className="flex items-center justify-center gap-2">
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent" />
                  <span className="text-xs text-muted-foreground">Loading older messages...</span>
                </div>
              ) : (
                <span className="text-xs text-muted-foreground">Scroll up to load more</span>
              )}
            </div>
          )}
          {messages.map((message, idx) => {
            const isUser = message.role === 'user';
            if (isUser && message.parts.some((p) => isUserAnswerPart(p))) {
              return null;
            }
            const isNew = !prevIdsRef.current?.has(message.id);

            const Wrapper = isNew ? motion.div : 'div';
            const motionProps = isNew
              ? {
                  initial: { opacity: 0, y: 4 } as const,
                  animate: { opacity: 1, y: 0 } as const,
                  transition: { duration: 0.2, ease: 'easeOut' as const },
                }
              : {};

            const isLastMsg = idx === messages.length - 1;
            const hasActiveTool = message.parts.some(
              (p) => isToolOrDynamicToolUIPart(p) && p.state !== 'output-available'
            );
            const showInlineShimmer = isLoading && isLastMsg && !isUser && !hasActiveTool;

            return (
              <Wrapper
                key={message.id}
                {...motionProps}
                className={`group flex flex-col ${isUser ? 'items-end' : 'items-start w-full'} min-w-0 max-w-full gap-1`}
              >
                {isUser ? (
                  <UserMessage parts={message.parts} />
                ) : (
                  <AssistantMessage
                    parts={message.parts}
                    messageId={message.id}
                    messages={messages}
                    showTrailingShimmer={showInlineShimmer}
                  />
                )}
                {!(isLoading && idx === messages.length - 1 && !isUser) && (
                  <MessageActions
                    message={message}
                    isLast={idx === lastAssistantIdx}
                    isUser={isUser}
                    onRetry={onRetry}
                    appContext={appContext}
                    analyticsCtx={analyticsCtx}
                    onPersistFeedback={persistFeedback}
                    onClearFeedback={deletePersistedFeedback}
                    feedback={feedbackMap[message.id]}
                    onFeedbackSet={handleFeedbackSet}
                    createdAt={
                      (message as UIMessage & { createdAt?: Date }).createdAt ??
                      firstSeenRef.current.get(message.id)
                    }
                  />
                )}
              </Wrapper>
            );
          })}
          {isLoading &&
            (messages.length === 0 || messages[messages.length - 1].role === 'user') && (
              <TextShimmer duration={1.5}>Thinking ...</TextShimmer>
            )}
          <div ref={bottomRef} />
        </div>
      </div>
      {!isAtBottom && (
        <button
          type="button"
          className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-1.5 rounded-full border bg-background px-3 py-1.5 text-xs text-muted-foreground shadow-md hover:text-foreground transition-colors animate-in fade-in slide-in-from-bottom-2 duration-200"
          onClick={() => scrollToBottom()}
        >
          <RiArrowDownLine className="h-3.5 w-3.5" />
          New messages
        </button>
      )}
    </div>
  );
}
